import { Link } from 'react-router-dom';
import { formatNumber } from '../utils.js';

/**
 * "Scenario KPIs By Amount" — right-hand widget on the Daily
 * Dashboard. Mirrors the legacy `sec_125`: a Top-N list of the
 * scenarios with the highest aggregated `value` against the
 * license's main KPI metric (over the date range driven by the
 * page's date-range dropdown).
 *
 * Empty-state when the rollup has zero rows for the active range —
 * matches the legacy "No KPI data available" copy.
 */
export interface ScenarioKpisByAmountRow {
  fusionId: string;
  scenarioName: string;
  amount: number;
}

export interface ScenarioKpisByAmountTableProps {
  rows: ReadonlyArray<ScenarioKpisByAmountRow>;
}

export function ScenarioKpisByAmountTable({ rows }: ScenarioKpisByAmountTableProps): JSX.Element {
  const maxAmount = rows.reduce((m, r) => (r.amount > m ? r.amount : m), 0);
  const totalAmount = rows.reduce((s, r) => s + r.amount, 0);
  return (
    <section className="overflow-hidden rounded-lg border border-foreground/10 bg-foreground/[0.02]">
      <header className="border-b border-foreground/10 px-3 py-2 text-sm font-semibold text-foreground">
        Scenario KPIs By Amount
      </header>
      {rows.length === 0 ? (
        <div className="flex min-h-[200px] items-center justify-center px-3 py-6 text-center text-sm text-foreground/50">
          No KPI data available
        </div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-foreground/55">
              <tr className="border-b border-foreground/10">
                <th className="px-3 py-2 text-left font-medium">Scenario</th>
                <th
                  className="px-2 py-2 text-right font-medium text-foreground"
                  style={{ backgroundColor: 'rgba(180, 83, 9, 0.18)' }}
                >
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pct = maxAmount > 0 ? Math.min(100, (r.amount / maxAmount) * 100) : 0;
                return (
                  <tr
                    key={r.fusionId}
                    className="border-b border-foreground/5 align-middle last:border-b-0"
                  >
                    <td className="truncate px-3 py-2 text-foreground" title={r.scenarioName}>
                      <Link
                        to={`/scenarios?scenarioId=${encodeURIComponent(r.fusionId)}`}
                        className="text-foreground hover:underline"
                      >
                        {r.scenarioName}
                      </Link>
                    </td>
                    <td className="relative px-2 py-2 text-right text-foreground">
                      <div className="absolute inset-0">
                        <div
                          className="h-full"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: 'rgb(180, 83, 9)',
                            opacity: 0.25,
                          }}
                        />
                      </div>
                      <span className="relative font-mono">${formatNumber(r.amount)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-foreground/[0.04] text-sm">
              <tr className="border-t border-foreground/15">
                <td className="px-3 py-2 text-left font-semibold text-foreground">Total</td>
                <td
                  className="px-2 py-2 text-right font-mono font-semibold text-foreground"
                  style={{ backgroundColor: 'rgba(180, 83, 9, 0.18)' }}
                >
                  ${formatNumber(totalAmount)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
